import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchSharedData, SharedDashboardData } from '../hooks/useSharing'
import { formatKpiEur } from '../lib/formatters'

const SECTION_LABELS: Record<string, string> = {
  executive: 'Executive Cockpit',
  revenue: 'Revenue Operations',
  margin: 'Margin Control',
  billing: 'Billing & AR',
  recurring: 'Recurring Revenue',
}

export function SharedDashboard() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<SharedDashboardData | null>(null)
  const [error, setError] = useState<{ status?: number; message: string } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) return
    fetchSharedData(token)
      .then(setData)
      .catch((err) => setError({ status: (err as { status?: number }).status, message: err.message }))
      .finally(() => setLoading(false))
  }, [token])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--km-bg-deep)' }}>
        <p className="text-sm" style={{ color: 'var(--km-text-muted)' }}>Caricamento dashboard...</p>
      </div>
    )
  }

  if (error) {
    const isExpired = error.status === 410
    const isNotFound = error.status === 404 || error.status === 401
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--km-bg-deep)' }}>
        <div className="text-center max-w-md space-y-3">
          <div className="text-4xl">{isExpired ? '⏱' : isNotFound ? '🔍' : '⚠️'}</div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
            {isExpired ? 'Link non più disponibile' : isNotFound ? 'Link non trovato' : 'Errore'}
          </h1>
          <p className="text-sm" style={{ color: 'var(--km-text-muted)' }}>
            {error.message}
          </p>
          <p className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
            {isExpired
              ? 'Questo link è scaduto o è stato revocato. Contatta chi ti ha condiviso il link per riceverne uno nuovo.'
              : 'Verifica di avere il link corretto.'}
          </p>
        </div>
      </div>
    )
  }

  if (!data) return null

  const expiresAt = new Date(data.expires_at)

  return (
    <div className="min-h-screen" style={{ background: 'var(--km-bg-deep)' }}>
      {/* Topbar read-only */}
      <header
        className="border-b px-6 py-3 flex items-center justify-between"
        style={{ background: 'rgba(8,11,18,0.97)', borderColor: 'var(--km-border)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
            style={{ background: 'var(--km-gradient-brand)' }}
          >
            K
          </div>
          <span className="text-xs font-bold text-white tracking-widest">KOREMARGIN</span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            style={{ background: 'rgba(99,102,241,0.2)', color: 'var(--km-primary)' }}
          >
            Sola lettura
          </span>
        </div>
        <p className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
          Valido fino al {expiresAt.toLocaleDateString('it-IT')}
        </p>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Sezioni condivise */}
        {data.sections.map((section) => {
          const sectionData = data.data[section as keyof typeof data.data]
          if (!sectionData) return null
          return (
            <section key={section} className="space-y-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--km-text-primary)' }}>
                {SECTION_LABELS[section] ?? section}
              </h2>
              <KpiGrid sectionData={sectionData} section={section} />
            </section>
          )
        })}

        {/* Footer */}
        <footer className="pt-4 border-t text-center" style={{ borderColor: 'var(--km-border)' }}>
          <p className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
            Dashboard generata da Koremargin · Dati in sola lettura · Link valido fino al{' '}
            {expiresAt.toLocaleDateString('it-IT')}
          </p>
        </footer>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Griglia KPI adattiva per sezione
// ---------------------------------------------------------------------------

const MONEY_KEYS = new Set([
  'revenue_ytd', 'revenue_mtd', 'confirmed_orders_value', 'pipeline_value',
  'billing_outstanding', 'total_invoiced', 'total_paid', 'overdue_amount',
  'mrr', 'arr', 'forecast_total', 'forecast_12m',
  'avg_order_value', 'total_cost', 'total_margin',
])

const PCT_KEYS = new Set([
  'gross_margin_pct', 'avg_margin_pct', 'pipeline_coverage_pct',
  'collection_rate_pct', 'churn_rate', 'nrr',
])

const LABEL_MAP: Record<string, string> = {
  revenue_ytd: 'Revenue YTD',
  revenue_mtd: 'Revenue MTD',
  gross_margin_pct: 'Margine lordo',
  confirmed_orders_value: 'Ordini confermati',
  pipeline_value: 'Pipeline',
  billing_outstanding: 'AR in sospeso',
  overdue_amount: 'Scaduto',
  collection_rate_pct: 'Tasso incasso',
  mrr: 'MRR',
  arr: 'ARR',
  forecast_12m: 'Forecast 12m',
  avg_margin_pct: 'Margine medio',
  avg_order_value: 'Ticket medio',
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—'
  const n = Number(value)
  if (isNaN(n)) return String(value)
  if (MONEY_KEYS.has(key)) return formatKpiEur(n)
  if (PCT_KEYS.has(key)) return `${n.toFixed(1)}%`
  if (Number.isInteger(n)) return n.toLocaleString('it-IT')
  return n.toLocaleString('it-IT', { maximumFractionDigits: 2 })
}

function KpiGrid({ sectionData, section }: { sectionData: Record<string, unknown>; section: string }) {
  // Mostra solo i KPI scalari di primo livello (non array/oggetti annidati)
  const priorityKeys = ['revenue_ytd', 'gross_margin_pct', 'confirmed_orders_value',
    'billing_outstanding', 'mrr', 'arr', 'avg_margin_pct', 'pipeline_value',
    'collection_rate_pct', 'forecast_12m']

  const scalarEntries = Object.entries(sectionData).filter(
    ([, v]) => v !== null && typeof v !== 'object' && !Array.isArray(v)
  )

  // Prima le chiavi prioritarie, poi le restanti
  const sorted = [
    ...priorityKeys.filter((k) => k in sectionData),
    ...scalarEntries.map(([k]) => k).filter((k) => !priorityKeys.includes(k)),
  ].slice(0, 8)

  if (sorted.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed p-6 text-center text-sm"
        style={{ borderColor: 'var(--km-border)', color: 'var(--km-text-muted)' }}
      >
        Nessun dato disponibile per questa sezione.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {sorted.map((key) => (
        <div
          key={`${section}-${key}`}
          className="rounded-xl border p-4 space-y-1"
          style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
        >
          <p className="text-xs truncate" style={{ color: 'var(--km-text-muted)' }}>
            {LABEL_MAP[key] ?? key.replace(/_/g, ' ')}
          </p>
          <p className="text-xl font-semibold whitespace-nowrap overflow-hidden text-ellipsis" style={{ color: 'var(--km-text-primary)' }}>
            {formatValue(key, sectionData[key])}
          </p>
        </div>
      ))}
    </div>
  )
}
